import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import {
  decideRemoteOfficeHoursQuota,
  getRemoteOfficeHoursQuotaRequestsForReportingManager,
} from '../../services/RemoteOfficeHoursService';
import { getAdditionalWfhReportees } from '../../services/AdditionalWfhQuotaService';
import { getManagerProjects, getUserData } from '../../services/UserService';
import KeycloakService from '../../services/KeycloakService';
import { ERROR_KEY, SHOW_NOTIFICATION } from '../../constants';
import PaginationComponent from '../../components/Pagination/Pagination';
import GenericLoader from '../../components/Loader/GenericLoader';
import RemoteOfficeHoursQuotaCard from '../../components/RemoteOfficeHoursQuota/RemoteOfficeHoursQuotaCard';
import RemoteOfficeHoursQuotaFilters from '../../components/RemoteOfficeHoursQuota/RemoteOfficeHoursQuotaFilters';

const defaultFilters = () => ({
  status: 'PENDING',
  projectId: '',
  quotaMonth: '',
  empId: '',
});

const SeniorManagerRemoteOfficeHoursComponent = () => {
  const reportingManagerId = KeycloakService.getUserDetail();
  const dispatch = useDispatch();
  const [requests, setRequests] = useState([]);
  const [reportees, setReportees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState({});
  const [loader, setLoader] = useState(true);
  const [filters, setFilters] = useState(defaultFilters);
  const [page, setPage] = useState(1);
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [refresh, setRefresh] = useState(false);

  const notify = (type, message) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: { open: true, type, message },
    });
  };

  const getProjectName = (projectId) => {
    const match = projects.find(
      (p) => String(p.projectId || p.id) === String(projectId),
    );
    return match?.projectName || match?.name || '';
  };

  const loadReportees = useCallback(async () => {
    const response = await getAdditionalWfhReportees('', 0, 200);
    if (!response.isError) {
      setReportees(response.data?.data?.content || response.data?.content || []);
    }
  }, []);

  const loadProjects = useCallback(async () => {
    const response = await getManagerProjects(reportingManagerId, null, null, 0);
    if (response?.status === 200) {
      setProjects(response.data?.content || []);
    }
  }, [reportingManagerId]);

  const loadRequests = useCallback(async () => {
    setLoader(true);
    const response = await getRemoteOfficeHoursQuotaRequestsForReportingManager(
      filters,
      pageNo,
      pageSize,
    );
    setLoader(false);
    if (!response.isError) {
      const pageData = response.data?.data || {};
      const content = pageData.content || [];
      setRequests(content);
      setTotalPages(pageData.totalPages || 0);

      const empMap = {};
      await Promise.all(
        content.map(async (req) => {
          if (empMap[req.employeeId]) {
            return;
          }
          try {
            const user = await getUserData(req.employeeId);
            if (!user.isError) {
              empMap[req.employeeId] = `${user.data.firstName} ${user.data.lastName}`;
            }
          } catch {
            // fallback
          }
        }),
      );
      setEmployees((prev) => ({ ...prev, ...empMap }));
    } else {
      notify(ERROR_KEY, response.message);
    }
  }, [filters, pageNo, pageSize]);

  useEffect(() => {
    loadReportees();
    loadProjects();
  }, [loadReportees, loadProjects]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests, refresh]);

  useEffect(() => {
    setPageNo(0);
    setPage(1);
    setRefresh((prev) => !prev);
  }, [filters]);

  const pageChange = (event, value) => {
    setPageNo(value - 1);
    setPage(value);
    setRefresh((prev) => !prev);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPageNo(0);
    setPage(1);
    setRefresh((prev) => !prev);
  };

  const handleAction = async (request, status) => {
    const response = await decideRemoteOfficeHoursQuota({
      id: request.id,
      employeeId: request.employeeId,
      status,
      approverId: reportingManagerId,
    });
    if (!response.isError) {
      notify('success', `Quota ${status.toLowerCase()}`);
      setRefresh((prev) => !prev);
    } else {
      notify(ERROR_KEY, response.message);
    }
  };

  return (
    <Box className="main-content-overlay">
      <div className="main-content-div">
        <Typography variant="h6" sx={{ mb: 1, mt: 1 }}>
          Remote Office Hours
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Quota requests routed to you when the applicant is the sole project manager.
        </Typography>

        <RemoteOfficeHoursQuotaFilters
          filters={filters}
          setFilters={setFilters}
          projects={projects}
          showEmployeeFilter
          employees={reportees}
        />

        {loader ? (
          <GenericLoader width="100%" height={400} />
        ) : requests.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '40vh',
            }}
          >
            <Typography color="text.secondary">
              No Remote Office Hours quota requests found.
            </Typography>
          </Box>
        ) : (
          requests.map((req) => (
            <RemoteOfficeHoursQuotaCard
              key={req.id}
              request={req}
              variant="manager"
              projectName={getProjectName(req.projectId)}
              employeeName={employees[req.employeeId]}
              onApprove={(item) => handleAction(item, 'APPROVED')}
              onReject={(item) => handleAction(item, 'REJECTED')}
            />
          ))
        )}

        {requests.length !== 0 && (
          <Box className="page-holder" sx={{ marginBottom: '10px' }}>
            <PaginationComponent
              count={totalPages}
              page={page}
              pageChange={pageChange}
              pageSize={pageSize}
              handlePageSizeChange={handlePageSizeChange}
            />
          </Box>
        )}
      </div>
    </Box>
  );
};

export default SeniorManagerRemoteOfficeHoursComponent;
